import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { jwtDecode } from 'jwt-decode'
import {
  Card,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Alert,
  Stack,
  Center,
  Box,
  Anchor,
} from '@mantine/core'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../lib/api'

interface AccessTokenPayload {
  uid: string
  user_id: string
  schema: string
  perfil: string
  exp: number
  iat: number
  jti: string
  token_type: 'access'
}

interface MeResponse {
  id: string
  nome_completo: string
  email: string
  perfil: string
  ativo: boolean
  registro_profissional?: string
  ultimo_acesso_em?: string
}

const schema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Informe a senha'),
  totp: z.string().optional(),
})
type FormData = z.infer<typeof schema>

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [erro, setErro] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [exigeTotp, setExigeTotp] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    setErro(null)
    try {
      // 1. Autenticar — retorna apenas { access, refresh }
      const resp = await api.post('/auth/login/', data)
      const tokens = { access: resp.data.access as string, refresh: resp.data.refresh as string }

      // 2. Extrair schema do payload do JWT
      const payload = jwtDecode<AccessTokenPayload>(tokens.access)

      // 3. Buscar dados completos do usuário (email, nome_completo, etc.)
      let me: MeResponse
      try {
        const meResp = await api.get<MeResponse>('/auth/me/', {
          headers: { Authorization: `Bearer ${tokens.access}` },
        })
        me = meResp.data
      } catch {
        setErro('Não foi possível carregar os dados do usuário. Tente novamente.')
        return
      }

      // 4. Salvar store com dados completos e navegar
      login(tokens, {
        id: me.id,
        email: me.email,
        nomeCompleto: me.nome_completo,
        perfil: me.perfil,
        schema: payload.schema,
      })
      navigate('/dashboard')
    } catch (e: unknown) {
      const err = e as {
        response?: { status: number; data?: { detail?: string } }
      }
      const status = err?.response?.status
      const detail = err?.response?.data?.detail ?? ''
      if (status === 401 && /2FA/i.test(detail)) {
        setExigeTotp(true)
        setErro(detail)
      } else if (status === 401) {
        setErro('Email ou senha incorretos.')
      } else {
        setErro('Erro de conexão. Tente novamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Center h="100vh" bg="var(--background)">
      <Box w={380}>
        <Stack align="center" mb="xl" gap="xs">
          <Text size="2.5rem" style={{ lineHeight: 1 }}>❄️</Text>
          <Title order={1} size="h2" ta="center">
            PMOC_123
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Gestão de Manutenção HVAC
          </Text>
        </Stack>

        <Card shadow="sm" radius="md" withBorder>
          <form onSubmit={handleSubmit(onSubmit)} data-testid="login-form" noValidate>
            <Stack gap="md">
              <TextInput
                label="Email"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                data-testid="login-email"
                {...register('email')}
              />
              <PasswordInput
                label="Senha"
                autoComplete="current-password"
                error={errors.password?.message}
                data-testid="login-password"
                {...register('password')}
              />
              {exigeTotp && (
                <TextInput
                  label="Código 2FA (6 dígitos)"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  data-testid="login-totp"
                  {...register('totp')}
                />
              )}
              {erro && (
                <Alert color="red" variant="light" data-testid="login-error" role="alert">
                  {erro}
                </Alert>
              )}
              <Button type="submit" loading={loading} fullWidth mt="xs" data-testid="login-submit">
                Entrar
              </Button>
              <Text size="sm" ta="center">
                <Anchor
                  component={Link}
                  to="/recuperar-senha"
                  data-testid="link-esqueci-senha"
                >
                  Esqueci minha senha
                </Anchor>
              </Text>
            </Stack>
          </form>
        </Card>
      </Box>
    </Center>
  )
}
